import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import { areArraysEqual } from '@/utils/arrayUtils';
import { usePlayerApi } from '@/composables/api/usePlayerApi';
import type { Product as UiProduct } from '@/types/Product';
import { mapApiProductsToUiProducts } from '@/services/mappers/productMapper';
import { debounce } from '@/utils/debounce';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { useAutoExtendWindow } from '@/composables/search/useAutoExtendWindow';
import { maxWeeklyPreset, nightsInRange, type DurationMode } from '@/utils/filters/durationMode';
import { buildDurationPlan } from '@/utils/filters/durationPlan';
import { resolveDurationFlex } from '@/utils/searchDefaults/searchDefaultsResolver';
import { loadIbeSearchDefaults } from '@/composables/filters/useSearchDefaults';
import { UrlParameterService, type UrlParameters } from '@/services/url/urlParameterService';
import { getProductList as getNbcProductList, type NBCProductListItem } from '@/services/api/nbc';
import { createNbcLookupMap } from '@/utils/nbc/nbcHelpers';
import { NetworkError } from '@/types/errors';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration';
import { usePlayerAvailableValuesStore } from '@/stores/playerAvailableValues';
import { ProductListFiltersStorage } from '@/services/storage/productListFiltersStorage';

export enum SortOption {
  RECOMMENDATION = 'Recommendation',
  PRICE_ASC = 'Price',
}

/**
 * Filter state interface for persistence and initialization
 *
 * Used by:
 * - localStorage persistence (Pinia persist plugin)
 * - URL parameter mapping (useFilterInitializer)
 * - ibeSearchDefaults mapping (useFilterInitializer)
 * - setInitialState() silent loading
 */
export interface FilterState {
  selectedCountries?: string[];
  dateRange?: DateRange;
  roomOccupancy?: RoomOccupancy;
  duration?: number;
  durationMode?: DurationMode;
  selectedAccommodationProperties?: string[];
  sortBy?: SortOption;
  fixedBookableOnly?: boolean;
  itemsPerPage?: number;
  currentPage?: number;
}

/**
 * Store for Product List active filters and search state
 *
 * Features:
 * - Search criteria (countries, dates, occupancy)
 * - Active AccommodationProperties filters
 * - Sorting preference
 * - Pagination state
 *
 * Usage:
 * ```ts
 * const store = useProductListFiltersStore();
 * store.setCountries(['ES', 'IT']);
 * store.setDateRange([new Date('2024-07-01'), new Date('2024-07-31')]);
 * ```
 */
export const useProductListFiltersStore = defineStore('productListFilters', () => {
  // Initialize usePlayerApi composable for API calls with cancellation
  const { productList } = usePlayerApi();

  const selectedCountries = ref<string[]>([]);
  const dateRange = ref<DateRange>(null);
  const roomOccupancy = ref<RoomOccupancy>({ rooms: [] });
  const duration = ref<number>(7);
  const durationMode = ref<DurationMode>('preset');
  const durationFlex = computed(() => resolveDurationFlex(loadIbeSearchDefaults()));

  // State - Filters
  const selectedAccommodationProperties = ref<string[]>([]);
  const forcedAccommodationProperties = ref<string[]>([]);
  const sortBy = ref<SortOption>(SortOption.RECOMMENDATION);
  const fixedBookableOnly = ref<boolean>(false);
  const currentPage = ref<number>(1);
  const itemsPerPage = ref<number>(12);
  const totalResults = ref<number>(0);

  // State - URL sync and localStorage control
  const urlSyncEnabled = ref(true);
  const localStorageEnabled = ref(true);
  const hasUserInteracted = ref(false);

  // Zero-result date-range auto-extension. On extend, mark user interaction so
  // the new period is persisted/synced, and reset pagination.
  const autoExtendConfigStore = useFilterConfigurationStore();
  const { autoExtendAttempted, applyAutoExtendedWindow, resetAutoExtend } = useAutoExtendWindow(
    dateRange,
    duration,
    () => maxWeeklyPreset(autoExtendConfigStore.durationOptions),
    () => {
      hasUserInteracted.value = true;
      currentPage.value = 1;
    }
  );

  // State - page-wide UI visibility
  const pageSearchBarVisible = ref(true);
  const pageFiltersVisible = ref(true);

  // State - NBC API
  const nbcDataMap = ref<Map<string, NBCProductListItem>>(new Map());
  const nbcLoading = ref(false);
  const nbcError = ref<string | null>(null);

  // Getters
  const paginationParams = computed(() => {
    const from = (currentPage.value - 1) * itemsPerPage.value + 1;
    return {
      resultsFrom: String(from),
      resultsTotal: String(itemsPerPage.value),
    };
  });

  const hasAccommodationFilters = computed(() => selectedAccommodationProperties.value.length > 0);

  const hasDateRange = computed(
    () =>
      dateRange.value !== null &&
      dateRange.value[0] instanceof Date &&
      dateRange.value[1] instanceof Date
  );

  const hasRoomOccupancy = computed(() => roomOccupancy.value.rooms.length > 0);

  const activeFiltersCount = computed(() => {
    return selectedAccommodationProperties.value.length;
  });

  const apiParams = computed(() => {
    if (!hasDateRange.value) {
      return null;
    }

    const [startDateObj, endDateObj] = dateRange.value!;

    const startDate = formatDateLocal(startDateObj);
    const endDate = formatDateLocal(endDateObj);

    // Calculate adults and children from roomOccupancy
    let adults = 0;
    const children: { age: number }[] = [];

    roomOccupancy.value.rooms.forEach((room) => {
      room.persons.forEach((person) => {
        if (person.type === 'adult') {
          adults++;
        } else if (person.type === 'child' && person.age !== undefined) {
          children.push({ age: person.age });
        }
      });
    });

    // Ensure at least 1 adult
    if (adults === 0) {
      adults = 1;
    }

    const availableValuesStore = usePlayerAvailableValuesStore();
    const countryGroupIdMap = availableValuesStore.countryGroupIdMap;
    const groupIds = selectedCountries.value
      .map((code) => countryGroupIdMap[code])
      .filter((id): id is number => id !== undefined);

    const mergedAccommodationProperties = [
      ...new Set([
        ...forcedAccommodationProperties.value,
        ...selectedAccommodationProperties.value,
      ]),
    ];

    const plan =
      durationMode.value === 'exact'
        ? null
        : buildDurationPlan(duration.value, nightsInRange(dateRange.value), durationFlex.value);
    const widened = plan && plan.durations.length > 1 ? plan : null;

    return {
      startDate,
      endDate,
      duration: duration.value,
      durations: widened?.durations,
      preferredDurations: widened?.preferred,
      adults,
      children,
      groupIds: groupIds.length > 0 ? groupIds : undefined,
      sortBy: sortBy.value === SortOption.RECOMMENDATION ? 'Priority' : sortBy.value,
      accommodationProperties:
        mergedAccommodationProperties.length > 0 ? mergedAccommodationProperties : undefined,
      requestBooking: fixedBookableOnly.value ? ('Exclude' as const) : undefined,
      ...paginationParams.value,
    };
  });

  // Getters - API state from composable
  const products = computed<UiProduct[]>(() => {
    const apiProducts = productList.data.value?.Products?.Product || [];
    const services = productList.data.value?.Services?.Service || [];

    return mapApiProductsToUiProducts(apiProducts, services, nbcDataMap.value);
  });

  const loading = computed(() => productList.loading.value);
  const error = computed(() => productList.error.value);

  /**
   * Composite keys ("AccommodationType.Hotel", …) that produced ≥1 result in
   * the latest GetProductList response. `null` means "fall back to the static
   * GetAvailableValues set"
   */
  const dynamicAvailableKeys = ref<Set<string> | null>(null);

  watch(
    () => productList.data.value,
    (data) => {
      if (!data) return; // loading / pre-first-response — keep previous value
      const overview = data.Filters?.AttributesOverview?.Attribute;
      if (!overview || overview.length === 0) {
        dynamicAvailableKeys.value = null;
        return;
      }
      const keys = new Set<string>();
      for (const a of overview) {
        if (a.Attribute) keys.add(a.Attribute);
      }
      dynamicAvailableKeys.value = keys.size > 0 ? keys : null;
    }
  );

  // URL Synchronization & LocalStorage
  function setInitialState(state: FilterState): void {
    // Temporarily disable URL sync
    const wasUrlSyncEnabled = urlSyncEnabled.value;
    urlSyncEnabled.value = false;

    try {
      // Apply state directly to refs (no setter functions to avoid triggering watchers)
      if (state.selectedCountries !== undefined) {
        selectedCountries.value = state.selectedCountries;
      }
      if (state.dateRange !== undefined) {
        dateRange.value = state.dateRange;
      }
      if (state.roomOccupancy !== undefined) {
        roomOccupancy.value = state.roomOccupancy;
      }
      if (state.duration !== undefined) {
        duration.value = state.duration;
      }
      if (state.durationMode !== undefined) {
        durationMode.value = state.durationMode;
      }
      if (state.selectedAccommodationProperties !== undefined) {
        selectedAccommodationProperties.value = state.selectedAccommodationProperties;
      }
      if (state.sortBy !== undefined) {
        sortBy.value = state.sortBy;
      }
      if (state.fixedBookableOnly !== undefined) {
        fixedBookableOnly.value = state.fixedBookableOnly;
      }
      if (state.itemsPerPage !== undefined) {
        itemsPerPage.value = state.itemsPerPage;
      }
      if (state.currentPage !== undefined) {
        currentPage.value = state.currentPage;
      }
    } finally {
      // Restore URL sync state
      urlSyncEnabled.value = wasUrlSyncEnabled;
    }
  }

  function loadFromLocalStorage(): FilterState | null {
    const parsed = ProductListFiltersStorage.read();
    if (!parsed) {
      return null;
    }

    if (Array.isArray(parsed.selectedAccommodationProperties)) {
      const configStore = useFilterConfigurationStore();
      const hidden = configStore.hiddenGroupIds;
      const disabled = configStore.disabledGroupIds;
      parsed.selectedAccommodationProperties = parsed.selectedAccommodationProperties.filter(
        (key: string) => {
          const groupId = configStore.findGroupIdForCompositeKey(key);
          if (groupId === null) return false;
          return !hidden.has(groupId) && !disabled.has(groupId);
        }
      );
    }

    return parsed;
  }

  /**
   * Drop persisted period (URL params + localStorage period fields)
   */
  function resetPersistedPeriod(): void {
    const url = new URL(window.location.href);
    ['date', 'todate', 'days'].forEach((p) => url.searchParams.delete(p));
    window.history.replaceState({}, '', url.toString());

    ProductListFiltersStorage.removePeriod();
  }

  function setForcedAccommodationProperties(keys: string[]): void {
    forcedAccommodationProperties.value = keys;
  }

  function setPageVisibility(flags: {
    pageSearchBarVisible: boolean;
    pageFiltersVisible: boolean;
  }): void {
    pageSearchBarVisible.value = flags.pageSearchBarVisible;
    pageFiltersVisible.value = flags.pageFiltersVisible;
  }

  function mapPropertiesToAttributes(properties: string[]): string[] | undefined {
    if (!properties || properties.length === 0) return undefined;

    const configStore = useFilterConfigurationStore();
    const tokens = configStore.resolveTokensForCompositeKeys(properties);

    return tokens.length > 0 ? tokens : undefined;
  }

  /**
   * Write the current filter state to the URL via `replaceState` immediately.
   * Honors forced fields, page-wide UI visibility, and the `urlSyncEnabled` /
   * `hasUserInteracted` gates. Use this when callers (e.g. sessionStorage
   * hydration on Back/Forward) need the URL to reflect new state synchronously
   * so an F5 reload preserves it.
   *
   * Visibility gating mirrors `useFilterInitializer`'s read-side rules:
   * fields whose owning UI is not rendered on this page are excluded — the
   * URL would only confuse a user who cannot interact with them.
   */
  function syncToUrl(): void {
    if (!urlSyncEnabled.value || !hasUserInteracted.value) {
      return;
    }

    const configStore = useFilterConfigurationStore();
    const forced = configStore.forcedParameterFields;
    const sbVisible = pageSearchBarVisible.value;
    const flVisible = pageFiltersVisible.value;

    const params: UrlParameters = {
      destinationId:
        !sbVisible || forced.has('dest') || selectedCountries.value.length === 0
          ? undefined
          : selectedCountries.value,
      startDate: !sbVisible || forced.has('date') ? undefined : dateRange.value?.[0] || undefined,
      endDate: !sbVisible || forced.has('todate') ? undefined : dateRange.value?.[1] || undefined,
      duration: !sbVisible || forced.has('days') ? undefined : duration.value,
      durationMode: forced.has('days') ? undefined : durationMode.value,
      roomOccupancy: !sbVisible || forced.has('pax') ? undefined : roomOccupancy.value,
      attributes: flVisible
        ? mapPropertiesToAttributes(selectedAccommodationProperties.value)
        : undefined,
      sortBy: flVisible
        ? sortBy.value === SortOption.PRICE_ASC
          ? 'price'
          : 'recommendation'
        : undefined,
      fixedBookableOnly: flVisible ? fixedBookableOnly.value : undefined,
      page: currentPage.value,
    };

    UrlParameterService.updateUrlParameters(params);
  }

  const syncToUrlDebounced = debounce(syncToUrl, 300);

  // Watch for changes and sync to URL (debounced)
  watch(
    [
      selectedCountries,
      dateRange,
      duration,
      durationMode,
      roomOccupancy,
      selectedAccommodationProperties,
      sortBy,
      fixedBookableOnly,
      currentPage,
    ],
    () => {
      syncToUrlDebounced();
    },
    { deep: true }
  );

  // Watch for changes and save to localStorage (only if enabled and user interacted).
  watch(
    [
      selectedCountries,
      dateRange,
      duration,
      roomOccupancy,
      selectedAccommodationProperties,
      sortBy,
      fixedBookableOnly,
      itemsPerPage,
    ],
    () => {
      if (!localStorageEnabled.value || !hasUserInteracted.value) {
        return;
      }

      try {
        const configStore = useFilterConfigurationStore();
        const forced = configStore.forcedParameterFields;
        const dateForced = forced.has('date') || forced.has('todate');
        const sbVisible = pageSearchBarVisible.value;
        const flVisible = pageFiltersVisible.value;

        const next: FilterState = { ...ProductListFiltersStorage.readRaw() };

        if (sbVisible) {
          next.selectedCountries = forced.has('dest') ? undefined : selectedCountries.value;
          next.dateRange = dateForced ? undefined : dateRange.value;
          next.roomOccupancy = forced.has('pax') ? undefined : roomOccupancy.value;
          next.duration = forced.has('days') ? undefined : duration.value;
        }
        if (flVisible) {
          next.selectedAccommodationProperties = selectedAccommodationProperties.value;
          next.sortBy = sortBy.value;
          next.fixedBookableOnly = fixedBookableOnly.value;
        }
        next.itemsPerPage = itemsPerPage.value;
        delete next.currentPage;

        ProductListFiltersStorage.write(next);
      } catch (error) {
        console.warn('[productListFilters] Failed to save to localStorage:', error);
      }
    },
    { deep: true }
  );

  // Actions - Search criteria

  // Store original setter functions
  const originalSetCountriesImpl = (countries: string[]): void => {
    if (areArraysEqual(selectedCountries.value, countries)) {
      return;
    }
    selectedCountries.value = countries;
    currentPage.value = 1;
  };

  const originalSetDateRangeImpl = (range: DateRange): void => {
    const newRangeString = JSON.stringify(range?.map((d) => (d ? d.getTime() : null)));
    const currentRangeString = JSON.stringify(
      dateRange.value?.map((d) => (d ? d.getTime() : null))
    );

    if (newRangeString === currentRangeString) {
      return;
    }

    dateRange.value = range;

    currentPage.value = 1;
  };

  const originalSetRoomOccupancyImpl = (occupancy: RoomOccupancy): void => {
    const newOccupancyString = JSON.stringify(occupancy);
    const currentOccupancyString = JSON.stringify(roomOccupancy.value);

    if (newOccupancyString === currentOccupancyString) {
      return;
    }

    roomOccupancy.value = occupancy;
    currentPage.value = 1;
  };

  const originalSetDurationImpl = (days: number): void => {
    if (duration.value === days) {
      return;
    }
    duration.value = days;
    currentPage.value = 1;
  };

  const originalSetDurationModeImpl = (mode: DurationMode): void => {
    if (durationMode.value === mode) {
      return;
    }
    durationMode.value = mode;
    currentPage.value = 1;
  };

  const originalToggleAccommodationPropertyImpl = (property: string): void => {
    const current = selectedAccommodationProperties.value;
    const index = current.indexOf(property);

    selectedAccommodationProperties.value =
      index > -1
        ? [...current.slice(0, index), ...current.slice(index + 1)]
        : [...current, property];
    currentPage.value = 1;
  };

  const originalSetAccommodationPropertiesImpl = (properties: string[]): void => {
    if (areArraysEqual(selectedAccommodationProperties.value, properties)) {
      return;
    }
    selectedAccommodationProperties.value = properties;
    currentPage.value = 1;
  };

  const originalSetSortByImpl = (option: SortOption): void => {
    if (sortBy.value === option) {
      return;
    }
    sortBy.value = option;
    currentPage.value = 1;
  };

  const originalSetFixedBookableOnlyImpl = (value: boolean): void => {
    if (fixedBookableOnly.value === value) {
      return;
    }
    fixedBookableOnly.value = value;
    currentPage.value = 1;
  };

  function setCountries(countries: string[]): void {
    hasUserInteracted.value = true;
    resetAutoExtend();
    return originalSetCountriesImpl(countries);
  }

  function setDateRange(range: DateRange): void {
    hasUserInteracted.value = true;
    resetAutoExtend();
    return originalSetDateRangeImpl(range);
  }

  function setRoomOccupancy(occupancy: RoomOccupancy): void {
    hasUserInteracted.value = true;
    resetAutoExtend();
    return originalSetRoomOccupancyImpl(occupancy);
  }

  function setDuration(days: number): void {
    hasUserInteracted.value = true;
    resetAutoExtend();
    return originalSetDurationImpl(days);
  }

  function setDurationMode(mode: DurationMode): void {
    hasUserInteracted.value = true;
    resetAutoExtend();
    return originalSetDurationModeImpl(mode);
  }

  function toggleAccommodationProperty(property: string): void {
    hasUserInteracted.value = true;
    return originalToggleAccommodationPropertyImpl(property);
  }

  function setAccommodationProperties(properties: string[]): void {
    hasUserInteracted.value = true;
    return originalSetAccommodationPropertiesImpl(properties);
  }

  function setSortBy(option: SortOption): void {
    hasUserInteracted.value = true;
    return originalSetSortByImpl(option);
  }

  function setFixedBookableOnly(value: boolean): void {
    hasUserInteracted.value = true;
    return originalSetFixedBookableOnlyImpl(value);
  }

  // Actions - Pagination
  function setCurrentPage(page: number): void {
    if (currentPage.value === page) {
      return;
    }
    hasUserInteracted.value = true;
    currentPage.value = page;
  }

  function setItemsPerPage(count: number): void {
    if (itemsPerPage.value === count) {
      return;
    }
    itemsPerPage.value = count;
    currentPage.value = 1;
  }

  function setTotalResults(count: number): void {
    if (totalResults.value === count) {
      return;
    }
    totalResults.value = count;
  }

  function resetPagination(): void {
    if (currentPage.value === 1) {
      return;
    }
    currentPage.value = 1;
  }

  // Actions - API
  async function fetchProductList(): Promise<void> {
    const params = apiParams.value;

    if (!params) {
      console.warn('Cannot fetch products: date range not set');
      return;
    }

    const result = await productList.call(params);

    const total = result?.ResultsTotal ? parseInt(result.ResultsTotal, 10) : 0;
    setTotalResults(Number.isNaN(total) ? 0 : total);

    // Zero results → widen the date window once
    if (result?.Success && total === 0 && !autoExtendAttempted.value && hasDateRange.value) {
      const configStore = useFilterConfigurationStore();
      const periodForced =
        configStore.forcedParameterFields.has('date') ||
        configStore.forcedParameterFields.has('todate') ||
        configStore.forcedParameterFields.has('days');
      if (!periodForced) {
        applyAutoExtendedWindow();
      }
    }
  }

  /**
   * Cancel pending product list request
   */
  function cancelProductList(): void {
    productList.cancel();
  }

  /**
   * Fetch NBC data for current products
   *
   * Fetches supplementary NBC teaser data for products from Player API.
   * NBC data is optional - application works without it.
   *
   * @example
   * await filtersStore.fetchProductList(); // Fetch Player data first
   * await filtersStore.fetchNbcData();      // Then enrich with NBC data
   */
  async function fetchNbcData(): Promise<void> {
    // Extract product IDs from current Player API results
    const apiProducts = productList.data.value?.Products?.Product || [];

    if (apiProducts.length === 0) {
      // No products to enrich
      nbcDataMap.value = new Map();
      return;
    }

    // Get product codes from services (same logic as mapper)
    const services = productList.data.value?.Services?.Service || [];
    const productIds: string[] = [];

    apiProducts.forEach((product) => {
      const serviceRefs = product.ServiceReferences?.ServiceReference || [];
      const serviceId = serviceRefs[0]?.ServiceId;
      const matchingService = services.find((s) => s.Id === serviceId);
      const accommodation =
        matchingService?.AccommodationService?.AccommodationSpecifics?.AccommodationSpecific?.[0];
      const productCode = accommodation?.ProductCode;

      if (productCode) {
        productIds.push(productCode);
      }
    });

    if (productIds.length === 0) {
      nbcDataMap.value = new Map();
      return;
    }

    // Fetch NBC data (non-blocking)
    nbcLoading.value = true;
    nbcError.value = null;

    try {
      const nbcResult = await getNbcProductList(productIds);

      // Create lookup map for fast O(1) access
      nbcDataMap.value = createNbcLookupMap(nbcResult.products);
    } catch (error) {
      if (error instanceof NetworkError) {
        console.warn('[NBC] API unavailable, using Player data only:', error.message);
        nbcError.value = 'network';
      } else {
        console.error('[NBC] Error fetching data:', error);
        nbcError.value = 'unknown';
      }
      // Non-breaking: clear NBC data but continue
      nbcDataMap.value = new Map();
    } finally {
      nbcLoading.value = false;
    }
  }

  const fetchNbcDataDebounced = debounce(() => {
    fetchNbcData();
  }, 300);

  watch(
    () => productList.data.value,
    (data) => {
      if (data) fetchNbcDataDebounced();
    }
  );

  // Actions - Reset
  function reset(): void {
    selectedCountries.value = [];
    dateRange.value = null;
    roomOccupancy.value = { rooms: [] };
    duration.value = 7;
    durationMode.value = 'preset';
    selectedAccommodationProperties.value = [];
    sortBy.value = SortOption.RECOMMENDATION;
    fixedBookableOnly.value = false;
    currentPage.value = 1;
    itemsPerPage.value = 12;
    totalResults.value = 0;
    resetAutoExtend();
  }

  /**
   * Wipe all persisted user-defined filters (localStorage) and reset in-memory
   * state, so the next SRP load rehydrates purely from `window.ibeSearchDefaults`.
   * Used by the PDP "Start new search" action.
   */
  function clearPersistedFilters(): void {
    ProductListFiltersStorage.removeAll();
    reset();
  }

  return {
    // State - Search criteria
    selectedCountries,
    dateRange,
    roomOccupancy,
    duration,
    durationMode,

    // State - Filters
    selectedAccommodationProperties,
    forcedAccommodationProperties,
    sortBy,
    fixedBookableOnly,

    // State - Pagination
    currentPage,
    itemsPerPage,
    totalResults,

    // State - URL sync & localStorage control
    urlSyncEnabled,
    localStorageEnabled,
    hasUserInteracted,

    // State - page-wide UI visibility
    pageSearchBarVisible,
    pageFiltersVisible,

    // Getters
    paginationParams,
    hasAccommodationFilters,
    hasDateRange,
    hasRoomOccupancy,
    activeFiltersCount,
    apiParams,

    // Getters - API state
    products,
    loading,
    error,

    // Getters - Dynamic filter narrowing (latest GetProductList Filters)
    dynamicAvailableKeys,

    // State - NBC API
    nbcDataMap,
    nbcLoading,
    nbcError,

    // Actions - Search criteria
    setCountries,
    setDateRange,
    setRoomOccupancy,
    setDuration,
    setDurationMode,

    // Actions - Filters
    toggleAccommodationProperty,
    setAccommodationProperties,
    setForcedAccommodationProperties,
    setPageVisibility,
    setSortBy,
    setFixedBookableOnly,

    // Actions - Pagination
    setCurrentPage,
    setItemsPerPage,
    setTotalResults,
    resetPagination,

    // Actions - API
    fetchProductList,
    cancelProductList,

    // Actions - Reset
    reset,
    clearPersistedFilters,

    // Actions - URL sync & localStorage
    setInitialState,
    loadFromLocalStorage,
    syncToUrl,
    resetPersistedPeriod,
  };
});
